import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { buildExclusivityQueryParams } from "@faire/retailer-visitor-shared/hooks/exclusivity/consts";
import { EXCLUSIVITY_AVAILABILITY_STALE_TIME } from "@faire/retailer-visitor-shared/hooks/exclusivity/useExclusivityAvailability";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { getCartToken } from "@faire/retailer-visitor-shared/serialized-data/getCartToken";
import { useSettingPdpPrefetchMoreFromBrand } from "@faire/retailer-visitor-shared/settings/useSettingPdpPrefetchMoreFromBrand";
import { PreloadedImageTracker } from "@faire/retailer-visitor-shared/views/product/preload/PreloadedImageTracker";
import {
  buildProductImageGridSrcSet,
  getImageGridMaxImagesVisible,
} from "@faire/retailer-visitor-shared/views/product/ProductImages/utils";
import { shouldShowRSCLIPP } from "@faire/retailer-visitor-shared/views/product/utils/shouldShowRSCLIPP";
import { prefetchLeanProduct } from "@faire/retailer-visitor-shared/views/product/utils/useLeanProductQuery";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import {
  getQueryFn as getExclusivityAvailabilityQueryFn,
  getQueryKey as getExclusivityAvailabilityQueryKey,
} from "@faire/web-api--source/endpoints/www/api/exclusivity-agreements/retailers/retailerToken/availability/get-hooks";
import {
  getQueryKey as getProductPromotionsQueryKey,
  getQueryFn as getProductPromotionsQueryFn,
} from "@faire/web-api--source/endpoints/www/api/product/productToken/product-promo-messages/get-hooks";
import fetchCart from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get";
import { getQueryKey as getCartQueryKey } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import {
  getQueryFn as getMoreFromBrandQueryFn,
  getQueryKey as getMoreFromBrandQueryKey,
} from "@faire/web-api--source/endpoints/www/api/v2/product/productToken/more-from-brand/get-hooks";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { getCartQueryParams } from "../../brand/BrandCart/hooks/consts";
import { BRAND_CART_STALE_TIME } from "../../brand/BrandCart/hooks/useBrandCart";
import { BRAND_INFO_ROW_STALE_TIME } from "../BrandInfoRow/consts";
import { MAX_NUM_OF_PRODUCTS_TO_DISPLAY } from "../BrandInfoRow/consts";
import { PRODUCT_PROMOTIONS_STALE_TIME } from "../ProductDetails/AddToCart/useProductPromotionsState";

export interface ProductDetails {
  productToken: string | undefined;
  bestImage: IImage | undefined;
  images: Array<IImage>;
  showVerticalImagery?: boolean;
  brandToken: string | undefined;
}

export function usePreloadNavigationToPDP() {
  const queryClient = useQueryClient();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { retailerToken = "" } = useRetailer();
  const { prefetch: nextPrefetch } = useNextRouterContext();
  const enablePdpPrefetchMoreFromBrand = useSettingPdpPrefetchMoreFromBrand();
  const { search } = useLocation();
  const { isDesktopAndAbove } = useViewport();

  const isRSC: boolean = shouldShowRSCLIPP(isLoggedInRetailer);

  return useCallback(
    (product: ProductDetails, productPageUrl: string) => {
      if (!product.productToken) {
        return;
      }

      commonPreloadNavigationToPDP(
        product,
        enablePdpPrefetchMoreFromBrand,
        isLoggedInRetailer,
        isDesktopAndAbove,
        queryClient,
        search
      );

      if (isRSC) {
        if (productPageUrl) {
          nextPrefetch(productPageUrl);
        }
        prefetchProductImages(product);
      } else {
        preloadNavigationToPDP(
          queryClient,
          product,
          isLoggedInRetailer,
          retailerToken
        );
      }
    },
    [
      queryClient,
      isLoggedInRetailer,
      retailerToken,
      isRSC,
      nextPrefetch,
      isDesktopAndAbove,
      enablePdpPrefetchMoreFromBrand,
      search,
    ]
  );
}

// Prefetch function that occurs for both RSC and non-RSC PDP pages
function commonPreloadNavigationToPDP(
  product: ProductDetails,
  enablePdpPrefetchMoreFromBrand: boolean,
  isLoggedInRetailer: boolean,
  isDesktopAndAbove: boolean,
  queryClient: QueryClient,
  search: string
): void {
  prefetchProductImages(product);
  if (
    enablePdpPrefetchMoreFromBrand &&
    isLoggedInRetailer &&
    isDesktopAndAbove &&
    product.productToken
  ) {
    // Prefetch more from brand
    const getMoreFromBrandQueryParams = {
      max_results: MAX_NUM_OF_PRODUCTS_TO_DISPLAY,
      referring_product_based_collection_token:
        ProductPagePaths.index.parseSearch(search).collectionRef,
    };

    queryClient.prefetchQuery({
      queryKey: getMoreFromBrandQueryKey(
        product.productToken,
        getMoreFromBrandQueryParams
      ),
      queryFn: getMoreFromBrandQueryFn(
        product.productToken,
        getMoreFromBrandQueryParams
      ),
      staleTime: BRAND_INFO_ROW_STALE_TIME,
    });
  }
}

function preloadNavigationToPDP(
  queryClient: QueryClient,
  product: ProductDetails,
  isLoggedInRetailer: boolean,
  retailerToken: string
): void {
  if (product.productToken) {
    // Prefetch product details
    prefetchLeanProduct(queryClient, product.productToken);

    // Prefetch product promotions
    queryClient.prefetchQuery({
      queryKey: getProductPromotionsQueryKey(product.productToken),
      queryFn: getProductPromotionsQueryFn(product.productToken),
      staleTime: PRODUCT_PROMOTIONS_STALE_TIME,
    });
  }

  if (product.brandToken && isLoggedInRetailer) {
    // Prefetch cart for logged in retailer
    const params = getCartQueryParams(product.brandToken);
    queryClient.prefetchQuery({
      queryKey: getCartQueryKey(getCartToken(), params),
      queryFn: () => {
        return fetchCart(getCartToken(), params);
      },
      staleTime: BRAND_CART_STALE_TIME,
    });

    // Prefetch exclusivity availability
    queryClient.prefetchQuery({
      queryKey: getExclusivityAvailabilityQueryKey(
        retailerToken,
        buildExclusivityQueryParams(product.brandToken)
      ),
      queryFn: getExclusivityAvailabilityQueryFn(
        retailerToken,
        buildExclusivityQueryParams(product.brandToken)
      ),
      staleTime: EXCLUSIVITY_AVAILABILITY_STALE_TIME,
    });
  }
}

function prefetchProductImages(product: ProductDetails): void {
  PreloadedImageTracker.get().clear();

  const { Image } = getWindowOrThrow();

  const showVerticalImagery = product.showVerticalImagery || false;

  getUniqueImages(product).forEach((image) => {
    const { src, srcSet } = buildProductImageGridSrcSet(
      image,
      showVerticalImagery
    );
    if (!src || !srcSet) {
      return;
    }

    const img = new Image();
    img.srcset = srcSet;
    img.src = src;

    PreloadedImageTracker.get().setInfo(src, {
      img,
      showVerticalImagery,
    });
  });
}

function getUniqueImages(product: ProductDetails): IImage[] {
  const images = product.images ? [...product.images] : [];
  const bestImage = product.bestImage;

  if (bestImage?.url && images.every((image) => image.url !== bestImage.url)) {
    images.unshift(bestImage);
  }

  return images.slice(0, getImageGridMaxImagesVisible());
}
